import { isKeyboardUser } from '@/features/shared/utils/detectKeyboardUser'
import { type CSSProperties, type ReactNode, type RefObject } from 'react'
import styled from 'styled-components'
import { typographyAttributes } from '@shipt/design-system-typography'

type OptionValue = string | number | undefined | null

export type OptionProps<TValue extends OptionValue> = {
  value: TValue
  label?: string
  className?: string
  selected?: boolean
  focusedIndex?: number
  index?: number
  disabled?: boolean
  hidden?: boolean
  isPII?: boolean
  innerRef?: RefObject<HTMLLIElement | null>
  style?: CSSProperties
  children?: ReactNode
}

export const Option = <TValue extends OptionValue>({
  children,
  index = 0,
  focusedIndex = 0,
  className = '',
  label = '',
  innerRef,
  value,
  isPII = false,
  ...restProps
}: OptionProps<TValue>) => {
  const { selected, disabled, ...includedRestProps } = restProps
  const isFocused = index === focusedIndex

  return (
    <OptionListItem
      {...typographyAttributes({ isPII })}
      {...includedRestProps}
      className={className}
      isFocused={isFocused}
      data-is-keyboard-user={isKeyboardUser()}
      role="option"
      ref={innerRef}
      aria-selected={index === focusedIndex}
    >
      {children || label || value}
    </OptionListItem>
  )
}

const OptionListItem = styled.li<{
  isFocused: boolean
  'data-is-keyboard-user'?: boolean
}>`
  cursor: pointer;
  background-color: ${({ theme, isFocused }) =>
    isFocused && theme.color.background.base.neutral.subtle};

  &[data-is-keyboard-user='true'] {
    margin: 2px;
    border-radius: ${({ isFocused }) => (isFocused ? 8 : 0)}px;
    outline: ${({ isFocused }) => (isFocused ? '2px black solid' : 'none')};
  }
`
